import type { Latam3DSServiceResponse, Latam3DSData, Latam3DSResponse } from './latam.3ds.types';
import { maskToken, logEnvironment } from './utils/latam.3ds.utils';

// URL base do backend (será configurada por ambiente)
const BACKEND_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000';

/**
 * Serviço para integração com Latam Gateway 3DS via backend
 */
export class Latam3DSService {
  /**
   * Passo 2: Obtém token de acesso para 3DS via backend
   */
  static async getCreditCardToken(): Promise<Latam3DSServiceResponse> {
    try {
      console.log('3DS Token - requisitando ao backend:', {
        url: `${BACKEND_BASE_URL}/latam/credit-card/token`,
      });
      const response = await fetch(`${BACKEND_BASE_URL}/latam/credit-card/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}), // Envia body vazio mas válido
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const maskedToken = typeof data?.token === 'string' ? maskToken(data.token) : 'token inválido';
      logEnvironment('Token recebido do backend', data.environment);
      console.log('🌍 Frontend 3DS - Token recebido do backend:', {
        maskedToken: maskedToken,
        environment: data.environment
      });

      return {
        success: true,
        token: data.token,
        environment: data.environment, // ✅ Retorna environment do backend
      };
    } catch (error) {
      console.error('Erro ao obter token 3DS:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /**
   * Passo 4: Processa pagamento com dados 3DS via backend
   */
  static async processCreditCardPayment(latamId: string, paymentData: Latam3DSResponse): Promise<Latam3DSServiceResponse> {
    try {
      const response = await fetch(`${BACKEND_BASE_URL}/latam/credit-card/order/${latamId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          address: paymentData.address,
          credit_card: {
            holder_name: paymentData.credit_card.holder_name,
            card_number: paymentData.credit_card.card_number,
            cvv: paymentData.credit_card.cvv,
            due_date: paymentData.credit_card.due_date,
            brand: paymentData.credit_card.brand,
            installments: 1, // Pode ser configurável
          },
          credentials: {
            payment_method_nonce: paymentData.credentials.payment_method_nonce,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return {
        success: true,
        message: 'Pagamento processado com sucesso',
        token: data.latam_id, // Retorna o ID do pedido
      };
    } catch (error) {
      console.error('Erro ao processar pagamento 3DS:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }

  /**
   * Converte dados do formulário para formato Latam 3DS
   */
  static convertFormDataToLatam3DS(
    orderId: string,
    customerData: any,
    creditCardData: any,
    addressData: any,
    orderValue?: number, // Adicionar valor do pedido
  ): Latam3DSData {
    // ✅ LOG DETALHADO: Dados sendo enviados para o Latam
    const latamData = {
      order: {
        id: orderId,
        value: orderValue || 0, // Incluir valor do pedido
      },
      customer: {
        name: customerData.fullName,
        document: customerData.document,
        email: customerData.email,
        phone: customerData.phone,
        birth: customerData.birth || '1999-01-01',
      },
      credit_card: {
        holder_name: creditCardData.cardholderName,
        card_number: creditCardData.cardNumber.replace(/\s/g, ''),
        cvv: creditCardData.cvv,
        due_date: creditCardData.expirationDate,
        brand: this.detectCardBrand(creditCardData.cardNumber),
        installments: creditCardData.installments || 1, // Adicionar installments
      },
      address: {
        street: addressData.street,
        number: addressData.number,
        neighborhood: addressData.neighborhood,
        complement: addressData.complement || '',
        zip: addressData.zipCode,
        city: addressData.city,
        state: addressData.state,
      },
    };

    // ✅ LOG BACKEND: Dados completos sendo enviados para verifyCard
    if (typeof window !== 'undefined' && (window as any).logToBackend) {
      (window as any).logToBackend(`Dados para verifyCard: ${JSON.stringify({
        orderId: latamData.order.id,
        orderValue: latamData.order.value,
        customerName: latamData.customer.name?.substring(0, 3) + '***',
        customerDocument: latamData.customer.document?.substring(0, 3) + '***',
        cardBrand: latamData.credit_card.brand,
        cardNumber: latamData.credit_card.card_number?.substring(0, 4) + '****',
        expirationDate: latamData.credit_card.due_date,
        cvv: latamData.credit_card.cvv?.substring(0, 1) + '**',
        holderName: latamData.credit_card.holder_name?.substring(0, 3) + '***',
        installments: latamData.credit_card.installments,
        addressStreet: latamData.address.street?.substring(0, 5) + '***',
        addressCity: latamData.address.city,
        addressState: latamData.address.state
      })}`, 'loading');
    }

    return latamData;
  }

  /**
   * Detecta a bandeira do cartão baseado no número
   */
  private static detectCardBrand(cardNumber: string): string {
    const digits = cardNumber?.replace(/\D/g, '') || '';
    if (!digits) return 'unknown';
  
    const first1 = digits.substring(0, 1);
    const first2 = parseInt(digits.substring(0, 2), 10);
    const first3 = parseInt(digits.substring(0, 3), 10);
    const first4 = parseInt(digits.substring(0, 4), 10);
    const first6 = parseInt(digits.substring(0, 6), 10);
  
    const inRange = (val: number, min: number, max: number) =>
      !Number.isNaN(val) && val >= min && val <= max;
  
    // Configuração centralizada de regras
    const brands = [
      {
        name: 'elo',
        match: () =>
          new Set([401178, 401179, 431274, 438935, 451416, 457393, 457631, 457632, 504175, 627780, 636297, 636368]).has(first6) ||
          inRange(first6, 506699, 506778) ||
          inRange(first6, 509000, 509999) ||
          inRange(first6, 650031, 650033) ||
          inRange(first6, 650035, 650051) ||
          inRange(first6, 650405, 650439) ||
          inRange(first6, 650485, 650538) ||
          inRange(first6, 650541, 650598) ||
          inRange(first6, 650700, 650718) ||
          inRange(first6, 650720, 650727) ||
          inRange(first6, 650901, 650978) ||
          inRange(first6, 651652, 651679) ||
          inRange(first6, 655000, 655019) ||
          inRange(first6, 655021, 655058)
      },
      {
        name: 'hipercard',
        match: () => first6 === 606282 || inRange(first6, 384100, 384199),
      },
      {
        name: 'diners',
        match: () =>
          inRange(first3, 300, 305) || first4 === 3095 || first2 === 36 || first2 === 38 || first2 === 39,
      },
      {
        name: 'jcb',
        match: () => inRange(first4, 3528, 3589),
      },
      {
        name: 'mastercard',
        match: () => inRange(first2, 51, 55) || inRange(first4, 2221, 2720),
      },
      {
        name: 'amex',
        match: () => first2 === 34 || first2 === 37,
      },
      {
        name: 'discover',
        match: () =>
          first4 === 6011 || inRange(first6, 622126, 622925) || inRange(first3, 644, 649) || first2 === 65,
      },
      {
        name: 'visa',
        match: () => first1 === '4',
      },
    ];
  
    // Encontra a primeira bandeira que casar
    for (const brand of brands) {
      if (brand.match()) return brand.name;
    }
  
    return 'unknown';
  }


}
